import { useState } from 'react';
import { IconArchive, IconArrowBackUp, IconCalendarEvent, IconChartBar, IconChevronLeft, IconClockHour4, IconCopy, IconDownload, IconFileText, IconHistory, IconLock, IconMapPin, IconPlayerPause, IconPlus, IconReceipt, IconShieldCheck, IconSparkles, IconEdit, IconTrendingUp, IconUpload, IconUser, IconX } from '@tabler/icons-react';
import { DataError, useList } from '@hana/data';
import { useT } from '@hana/i18n';
import { HeaderTool, Page, PageHeader } from '@hana/shell';
import { Button, Segmented, Skeleton, StateView, useToast, type ScreenState } from '@hana/ui';
import { financeScreenState, useFinanceAccess } from '../finance/access';
import { countOf, dk, dkt, kk, logOf, normRows, normStats, NORM_APR, NORM_PORT, NORM_RCPT, NORM_ST, NORM_SV, NORM_UNIT, resolveNorms, svOf, thrText, vnd, waitOf, type NormView, type NormVerView } from './normLogic';
import type { NormRow } from './normTypes';
import { Code, DocTags, initSel, NatTag, pill, tile } from './normParts';
import n from './norms.module.css';
import s from './normsCompact.module.css';
import './norms.i18n';

// Mint-Norms-iPad (1180): cây dịch vụ trái 230 + thẻ định mức; bấm thẻ → trang chi tiết 2 cột (nội dung + 340). Màn tiền, sau quyền money (?money=view). Chỉ đọc; nút ghi báo toast.
// Chưa làm: trang sửa (page=edit) và nhật ký toàn dịch vụ (page=log) — nút ghi báo toast.

type Rows = ReturnType<typeof normRows>;

function Cards({ rows, sup, pick }: { rows: Rows; sup: boolean; pick: (id: string, v: number | null) => void }) {
  const tr = useT();
  const nxTip = (nx: { v: string; st: keyof typeof NORM_ST; from: string; amt: number }) => tr('nrm.nextTip', { v: nx.v, st: NORM_ST[nx.st][0], from: dk(nx.from), amt: vnd(nx.amt) });
  return (
    <>
      {rows.map(({ n: r, x, old }) => {
        const nx = old ? null : r.next;
        const lab = tr('nrm.view', { code: r.code || tr('nrm.pending'), name: r.name });
        return (
          <button key={`${r.id}-${x.v}`} type="button" className={s.card} style={{ opacity: old ? 0.62 : 1 }} aria-label={lab} onClick={() => pick(r.id, old ? x.i : null)}>
            <span className={s.r1}>
              <Code n={r} w={52} h={32} />
              <b className={s.nm}>{r.name}</b>
              <b className={s.amt} title={tr('nrm.k.norm')}>{vnd(x.amt)} ₫</b>
            </span>
            <span className={s.r2}>
              <span className={s.tags}><DocTags docs={r.docs} /><NatTag nat={r.nat} /></span>
              <span title={tr('nrm.portUnit')}><IconMapPin size={13} stroke={1.8} aria-hidden="true" />{NORM_PORT[r.port]} · {NORM_UNIT[r.unit]}</span>
              <span title={tr('nrm.thr')}><IconTrendingUp size={13} stroke={1.8} aria-hidden="true" />{thrText(x.thrP, x.thrA)}</span>
              <span title={NORM_APR[r.apr][2]}><IconShieldCheck size={13} stroke={1.8} aria-hidden="true" />{NORM_APR[r.apr][0]} {NORM_APR[r.apr][1] && <b className={s.ktt}>{NORM_APR[r.apr][1]}</b>}</span>
              {sup && <span title={tr('nrm.c.rcpt')} style={{ color: r.rcpt ? undefined : 'var(--err)' }}><IconUser size={13} stroke={1.8} aria-hidden="true" />{r.rcpt ? NORM_RCPT[r.rcpt] : tr('nrm.unset')}</span>}
            </span>
            <span className={`${s.r2} ${n.lb}`}>
              <span className={n.st} style={pill(x.st)}>{NORM_ST[x.st][0]}</span>
              <span>{x.v} · {dk(x.from)} → {dk(x.to)}</span>
              {nx && <span title={nxTip(nx)} style={{ color: NORM_ST[nx.st][1] }}><IconCalendarEvent size={14} stroke={1.8} aria-hidden="true" />{nxTip(nx)}</span>}
            </span>
          </button>
        );
      })}
    </>
  );
}

function Detail({ r, vSel, setV, ask, setAsk, noWrite }: { r: NormView; vSel: number | null; setV: (i: number | null) => void; ask: boolean; setAsk: (b: boolean) => void; noWrite: () => void }) {
  const tr = useT();
  const logQ = useList('normLog', { limit: 200 });
  const view: NormVerView = vSel !== null ? r.vers[vSel] ?? r.cur : r.cur;
  const old = view.i !== r.cur.i;
  const S = normStats(r);
  const sug = Math.ceil(S.p90 / 10000) * 10000;
  const mxW = Math.max(S.lim, ...S.wavg) * 1.06;
  const flo = Math.min(r.cur.amt, ...S.wavg.filter((a) => a > 0)) * 0.7;
  const yp = (a: number) => Math.round(((a - flo) / Math.max(1, mxW - flo)) * 100);
  const sv = svOf(r.sv);
  const kpi: Array<[string, string, string]> = [
    [tr('nrm.k.norm'), `${vnd(view.amt)} ₫`, `/ ${NORM_UNIT[r.unit]}`],
    [tr('nrm.thr'), thrText(view.thrP, view.thrA), ''],
    [tr('nrm.apr'), `${NORM_APR[r.apr][0]}${NORM_APR[r.apr][1] ? ` ${NORM_APR[r.apr][1]}` : ''}`, ''],
    [tr('nrm.eff'), `${dk(view.from)} → ${dk(view.to)}`, NORM_ST[view.st][0]],
  ];
  const log = logOf(logQ.data?.items ?? [], r);
  const sw = (a: number) => (a > S.lim ? 'var(--d-amber)' : 'var(--d-blue)');
  return (
    <div className={s.dGrid}>
      <div className={s.col14}>
        {old && (
          <div className={n.oldBar} role="status">
            <IconHistory size={16} stroke={1.8} aria-hidden="true" />
            <span className={n.fw}>{tr('nrm.viewing', { v: view.v, st: NORM_ST[view.st][0] })}</span>
            <button type="button" className={n.back} onClick={() => setV(null)}><IconArrowBackUp size={14} stroke={1.8} aria-hidden="true" />{tr('nrm.current')}</button>
          </div>
        )}
        <div className={s.kpi}>
          {kpi.map(([k, v, sub]) => (
            <div key={k} className={s.kpiC}><div className={n.lb}>{k}</div><div className={s.kpiV}>{v}</div>{sub && <div className={n.lb}>{sub}</div>}</div>
          ))}
        </div>
        {ask && (
          <section className={n.askBox} aria-label={tr('nrm.askTitle')}>
            <div className={`${n.flex} ${n.askHead}`}>
              <IconSparkles size={16} stroke={1.9} aria-hidden="true" /><b>Ask HANA</b>
              <HeaderTool label={tr('nrm.closeSug')} icon={IconX} onClick={() => setAsk(false)} />
            </div>
            <div className={n.askRow}>
              <b className={n.askV}>{vnd(sug)} ₫</b>
              <span className={n.askD} title={tr('nrm.vsCur')}>{sug > r.cur.amt ? '+' : ''}{kk(sug - r.cur.amt)}</span>
              <span className={n.tx2} title={tr('nrm.sugThr')}>{thrText(r.cur.thrP || 5, r.cur.thrA)}</span>
              <span className={n.lb} title={tr('nrm.basis')}>P90 {kk(S.p90)} · {tr('nrm.over', { a: S.over, b: S.N })}</span>
            </div>
            <div><Button variant="primary" icon={<IconPlus size={16} aria-hidden="true" />} onClick={noWrite}>{tr('nrm.newVer')}</Button></div>
          </section>
        )}
        <section className={s.card2}>
          <div className={n.secH}>
            <IconChartBar size={16} stroke={1.8} aria-hidden="true" /><h3>{tr('nrm.actual')}</h3>
            <span className={n.leg}>
              <span className={n.flex}><span className={n.lnS} />{tr('nrm.k.norm')} {kk(r.cur.amt)}</span>
              <span className={n.flex}><span className={n.lnD} />{tr('nrm.limit')} {kk(S.lim)}</span>
            </span>
          </div>
          <div className={n.stats}>
            <div><div className={n.lb}>{tr('nrm.count')}</div><div className={n.sv}>{S.N}</div></div>
            <div><div className={n.lb}>{tr('nrm.avg')}</div><div className={n.sv} style={{ color: S.avg > r.cur.amt ? 'var(--warn)' : 'var(--tx)' }}>{vnd(Math.round(S.avg / 1000) * 1000)}</div></div>
            <div><div className={n.lb}>P90</div><div className={n.sv} style={{ color: S.p90 > S.lim ? 'var(--warn)' : 'var(--tx)' }}>{vnd(S.p90)}</div></div>
            <div><div className={n.lb}>{tr('nrm.overN')}</div><div className={n.sv} style={{ color: S.over ? 'var(--warn)' : 'var(--ok)' }}>{S.over}/{S.N}</div></div>
          </div>
          <div className={n.chart} role="img" aria-label={tr('nrm.chart')}>
            <div className={n.bars}>
              {S.wavg.map((a, i) => <span key={i} title={tr('nrm.week', { n: i + 1, avg: a ? vnd(Math.round(a / 1000) * 1000) : '—' })} style={{ height: `${a ? Math.max(4, yp(a)) : 0}%`, background: sw(a) }} />)}
            </div>
            <div aria-hidden="true" className={n.lineS} style={{ top: `${100 - yp(r.cur.amt)}%` }} />
            <div aria-hidden="true" className={n.lineD} style={{ top: `${100 - yp(Math.min(S.lim, mxW))}%` }} />
          </div>
          <div className={`${n.axis} ${n.lb}`}><span>23/Jul</span><span>22/Oct</span></div>
        </section>
        <section className={s.card2}>
          <div className={n.secH}><IconArchive size={16} stroke={1.8} aria-hidden="true" /><h3>{tr('nrm.versions')}</h3></div>
          <div className={n.vers}>
            {r.vers.slice().reverse().map((v) => (
              <button key={v.v} type="button" className={`${n.ver} ${s.ver}`} style={{ background: v.i === view.i ? 'var(--pri-bg)' : undefined }} aria-current={v.i === view.i} aria-label={`Xem ${v.v}`} onClick={() => setV(v.i === r.cur.i ? null : v.i)}>
                <b className={n.tx2}>{v.v}</b>
                <span className={n.vMain}>
                  <b>{vnd(v.amt)}</b><span className={n.tx2}>{thrText(v.thrP, v.thrA)}</span>
                  <span className={`${n.flex} ${n.lb}`}><IconCalendarEvent size={13} stroke={1.8} aria-hidden="true" />{dk(v.from)} → {dk(v.to)}</span>
                </span>
                <span className={n.st} style={pill(v.st)}>{NORM_ST[v.st][0]}</span>
              </button>
            ))}
          </div>
        </section>
      </div>
      <div className={s.col14}>
        <section className={s.card2}>
          <div className={n.secH}><IconFileText size={16} stroke={1.8} aria-hidden="true" /><h3>{tr('nrm.info')}</h3></div>
          <div className={n.info}>
            <div className={s.irow}><span className={n.lb}>{tr('nrm.service')}</span><span>{sv[0]} · {sv[1]}</span></div>
            <div className={s.irow}><span className={`${n.lb} ${n.flex}`}><IconReceipt size={13} stroke={1.8} aria-hidden="true" />{tr('nrm.docs')}</span><span className={n.tags}><DocTags docs={r.docs} /></span></div>
            <div className={s.irow}><span className={n.lb}>{tr('nrm.nature')}</span><span className={n.tags}><NatTag nat={r.nat} /></span></div>
            {r.grp === 'sup' && (
              <>
                <div className={s.irow}><span className={n.lb}>{tr('nrm.group')}</span><span>{tr('nrm.supFee', { sv: sv[3] })}</span></div>
                <div className={s.irow}><span className={n.lb}>{tr('nrm.c.rcpt')}</span><b className={n.fw}>{r.rcpt ? NORM_RCPT[r.rcpt] : '—'}</b></div>
              </>
            )}
          </div>
        </section>
        <section className={s.card2}>
          <div className={n.secH}><IconHistory size={16} stroke={1.8} aria-hidden="true" /><h3>{tr('nrm.history')}</h3></div>
          <div>
            {log.map((l, i) => (
              <div key={`${l.at}-${i}`} className={n.log} style={{ borderTop: i ? '1px solid var(--line)' : 0 }}>
                <span className={n.av} title={l.who} aria-hidden="true">{l.who.slice(0, 2)}</span>
                <div className={n.logB}>
                  <div className={n.flex}><b className={n.fw}>{l.who}</b><span className={n.lb}>{dkt(l.at)}</span></div>
                  <div className={n.lb}>{l.what}</div>
                  <div className={n.flex}><span className={n.lb} style={{ textDecoration: 'line-through' }}>{l.old}</span><span className={n.lb} aria-label={tr('nrm.to')}>→</span><b>{l.now}</b></div>
                </div>
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}

export function NormsCompact() {
  const tr = useT();
  const toast = useToast();
  const acc = useFinanceAccess();
  const rowsQ = useList('normRow', { limit: 200 });
  const [sv, setSv] = useState('CCF');
  const [grpF, setGrpF] = useState<'all' | NormRow['grp']>('all');
  const [pf, setPf] = useState('all');
  const [all, setAll] = useState(false);
  const [sel, setSel] = useState<{ id: string | null; v: number | null }>(() => ({ id: initSel(), v: null }));
  const [ask, setAsk] = useState(false);

  let state: ScreenState = financeScreenState(acc);
  if (state !== 'noperm' && !acc.force) {
    if (rowsQ.isLoading) state = 'loading';
    else if (rowsQ.isError) state = rowsQ.error instanceof DataError && rowsQ.error.code === 'noperm' ? 'noperm' : 'error';
    else if ((rowsQ.data?.items.length ?? 0) === 0) state = 'empty';
  }
  const noperm = state === 'noperm';
  const ready = state === 'ready';
  const all0 = resolveNorms(ready ? rowsQ.data?.items ?? [] : []);
  const noWrite = () => toast({ msg: tr('nrm.noWrite') });
  const soonTo = (what: string) => () => toast({ msg: tr('nrm.soon', { what }) });

  const waitAll = all0.filter((x) => x.vers.some((v) => v.st === 'wait')).length;
  const schedAll = all0.filter((x) => x.vers.some((v) => v.st === 'sched')).length;
  const std = normRows(all0, sv, pf, 'std', all);
  const sup = normRows(all0, sv, pf, 'sup', all);
  const nStd = countOf(all0, sv, pf, 'std');
  const nSup = countOf(all0, sv, pf, 'sup');
  const svT = svOf(sv);
  const showStd = grpF !== 'sup' && std.length > 0;
  const showSup = grpF !== 'std' && sup.length > 0;
  const selN = ready ? all0.find((x) => x.id === sel.id) ?? null : null;
  const pick = (id: string, v: number | null) => { setSel({ id, v }); setAsk(false); };
  const close = () => { setSel({ id: null, v: null }); setAsk(false); };

  const header = selN ? (
    <PageHeader
      lead={<button type="button" className={s.backBtn} aria-label={tr('nrm.back')} title={tr('nrm.back')} onClick={close}><IconChevronLeft size={20} aria-hidden="true" /></button>}
      title={selN.name}
      sub={`${selN.code || tr('nrm.pending')} · ${NORM_PORT[selN.port]} · ${NORM_UNIT[selN.unit]}`}
      actions={
        <>
          <HeaderTool label={tr('nrm.clone')} icon={IconCopy} onClick={noWrite} />
          <HeaderTool label={tr('nrm.stop')} icon={IconPlayerPause} onClick={noWrite} />
          <button type="button" className={n.ask} aria-label={tr('nrm.askTitle')} title={tr('nrm.askTitle')} onClick={() => setAsk(true)}>
            <IconSparkles size={16} stroke={1.9} aria-hidden="true" />{tr('nrm.suggest')}
          </button>
          <Button variant="primary" icon={<IconEdit size={16} aria-hidden="true" />} onClick={noWrite}>{tr('nrm.edit')}</Button>
        </>
      }
    />
  ) : (
    <PageHeader
      title={tr('nrm.title')}
      meta={ready ? (
        <>
          {waitAll > 0 && <span className={`${n.mchip} ${n.mWait}`} title={tr('nrm.waitT')} aria-label={tr('nrm.waitN', { n: waitAll })}><IconClockHour4 size={13} stroke={1.8} aria-hidden="true" />{waitAll}</span>}
          {schedAll > 0 && <span className={`${n.mchip} ${n.mSched}`} title={tr('nrm.schedT')} aria-label={tr('nrm.schedN', { n: schedAll })}><IconCalendarEvent size={13} stroke={1.8} aria-hidden="true" />{schedAll}</span>}
        </>
      ) : undefined}
      actions={
        <>
          <label className={n.port} title={tr('nrm.port')}>
            <IconMapPin size={15} stroke={1.8} aria-hidden="true" />
            <select aria-label={tr('nrm.port')} value={pf} onChange={(e) => setPf(e.target.value)}>
              <option value="all">{tr('nrm.allPorts')}</option>
              {Object.keys(NORM_PORT).filter((k) => k !== '*').map((k) => <option key={k} value={k}>{NORM_PORT[k]}</option>)}
            </select>
          </label>
          <HeaderTool label={tr('nrm.imp')} icon={IconUpload} onClick={noWrite} />
          <HeaderTool label={tr('nrm.exp')} icon={IconDownload} onClick={soonTo(tr('nrm.exp'))} />
          <HeaderTool label={tr('nrm.history')} icon={IconHistory} onClick={soonTo(tr('nrm.history'))} />
          <Button variant="primary" icon={<IconPlus size={16} aria-hidden="true" />} disabled={noperm} onClick={noWrite}>{tr('nrm.add')}</Button>
        </>
      }
    />
  );

  return (
    <Page kind="table">
      {header}
      <StateView
        state={noperm ? 'ready' : state}
        title={state === 'empty' ? tr('nrm.empty') : undefined}
        desc={state === 'empty' ? tr('nrm.emptyDesc') : undefined}
        icon={state === 'empty' ? <IconFileText size={26} stroke={1.8} /> : undefined}
        action={state === 'error' ? <Button onClick={() => void rowsQ.refetch()}>{tr('nrm.retry')}</Button> : undefined}
        skeleton={<Skeleton rows={9} />}
      >
        {noperm ? (
          <section className={n.none} role="status">
            <IconLock size={28} stroke={1.8} aria-hidden="true" />
            <b>{tr('nrm.noperm')}</b>
          </section>
        ) : selN ? (
          <Detail r={selN} vSel={sel.v} setV={(i) => setSel({ id: selN.id, v: i })} ask={ask} setAsk={setAsk} noWrite={noWrite} />
        ) : (
          <div className={s.body}>
            <nav className={s.tree} aria-label={tr('nrm.service')}>
              {NORM_SV.map(([k, name, color]) => {
                const on = k === sv;
                const w = waitOf(all0, k);
                const kids: Array<['all' | NormRow['grp'], string, number]> = [['all', tr('nrm.all'), countOf(all0, k, pf)], ['std', tr('nrm.std'), countOf(all0, k, pf, 'std')]];
                const ns = countOf(all0, k, pf, 'sup');
                if (ns) kids.push(['sup', tr('nrm.supFee', { sv: svOf(k)[3] }), ns]);
                return (
                  <div key={k}>
                    <button type="button" className={`${s.node} ${on ? s.nodeOn : ''}`} aria-current={on} onClick={() => { setSv(k); setGrpF('all'); close(); }}>
                      <span className={n.nIc} style={tile(color)}>{k}</span>
                      <span className={n.ell}>{name}</span>
                      {w > 0 && <span className={n.wait} title={tr('nrm.waitT')} aria-label={tr('nrm.waitS', { n: w })}><IconClockHour4 size={13} stroke={1.8} aria-hidden="true" />{w}</span>}
                      <span className={n.lb}>{countOf(all0, k, pf)}</span>
                    </button>
                    {on && kids.map(([g, l, c]) => (
                      <button key={g} type="button" className={`${s.kid} ${grpF === g ? s.kidOn : ''}`} aria-current={grpF === g} onClick={() => setGrpF(g)}>
                        <span className={n.ell}>{l}</span><span className={n.lb}>{c}</span>
                      </button>
                    ))}
                  </div>
                );
              })}
            </nav>
            <section className={s.list} aria-label={tr('nrm.paneLabel', { name: svT[1] })}>
              <div className={s.pbar}>
                <span className={n.pTile} style={tile(svT[2])}>{svT[0]}</span>
                <h2>{svT[1]}</h2>
                <span className={n.lb} title={tr('nrm.countT')}>{nStd + nSup}</span>
                <span className={n.right}>
                  <Segmented label={tr('nrm.versions')} value={all ? 'all' : 'cur'} onChange={(k) => setAll(k === 'all')} items={[{ key: 'cur', label: tr('nrm.eff') }, { key: 'all', label: tr('nrm.allVers') }]} />
                </span>
              </div>
              {showStd && (
                <>
                  <div className={s.gh}><h3>{tr('nrm.std')}</h3><span className={n.lb}>{nStd}</span></div>
                  <Cards rows={std} sup={false} pick={pick} />
                </>
              )}
              {showSup && (
                <>
                  <div className={s.gh}>
                    <h3>{tr('nrm.supFee', { sv: svT[3] })}</h3><span className={n.lb}>{nSup}</span>
                    <span className={n.only} title={tr('nrm.privTip')}><IconUser size={13} stroke={1.8} aria-hidden="true" />{tr('nrm.priv')}</span>
                    <span className={n.tag} title={tr('nrm.ndc')} style={{ color: 'var(--lb)', background: 'var(--sub)' }}>NDC</span>
                  </div>
                  <Cards rows={sup} sup pick={pick} />
                </>
              )}
              {!showStd && !showSup && <div className={n.empty}>{tr('nrm.none')}</div>}
            </section>
          </div>
        )}
      </StateView>
    </Page>
  );
}
